import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateParams, StateService } from '@uirouter/core';

import { CimaCore, Cloudinary, SessionStorage, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsCore } from 'store/cms/models';
import { MouseEventType, StorageToken } from 'core/constants';
import { Account, StreetAddress, User } from 'store/user/models';

@Component({
    selector: 'cbm-nav-menu-item',
    styleUrls: ['./cbm-menu-item.scss'],
    templateUrl: './cbm-menu-item.html',
    standalone: false
})
export class CbmNavMenuItemComponent implements OnInit, OnDestroy {
    @Input() public linkedAccounts: LocationRow[];
    @Input() public isMobile: boolean = false;
    @Output() public toggleMobileNav: EventEmitter<void> = new EventEmitter<void>();

    public menuIcon: MediaImageOptions;
    public caretIcon: MediaImageOptions;
    public checkmarkIcon: MediaImageOptions;
    public showChildItems: boolean;
    public showAll: boolean = false;
    public activeLinkedAccount: LocationRow;
    public linkedAccountWithActive: LocationRow[];
    public readonly ACCOUNTS_PREVIEW_LIMIT: number = 2;
    public isActiveAccountLinked: boolean = false;
    public getFullAddress: string;
    public os?: string;
    public acc?: string;

    public isOrionTenant: boolean;
    public displayName: string;
    public account: Account;
    public serviceAddress: StreetAddress;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private cimaCore: CimaCore;
    private sessionStorage: SessionStorage;
    private state: StateService;
    private cimaUserAccountGuid: string;
    private isOrion: boolean;

    constructor(state: StateService, sessionStorage: SessionStorage, xmStore: XmStore, cimaCore: CimaCore) {
        Object.assign(this, { state, sessionStorage, xmStore, cimaCore });
    }

    public ngOnInit(): void {
        this.isOrionTenant = this.sessionStorage.get(StorageToken.X_TENANT_ID) === 'ORION';
        this.cimaUserAccountGuid = this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID);
    
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.caretIcon = { mobile: Cloudinary.generateMediaFromCms(result.nav.caretIcon) };
            this.checkmarkIcon = { mobile: Cloudinary.generateMediaFromCms(result.icons.checkMarkIconGrey) };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User, { returnUndefined: true }), (user: User) => {
            if (this.cimaCore.isBusinessUser) {
                this.displayName = user?.displayName || '';
            } else {
                this.displayName = user?.loggedInUserName || '';
            }
            this.account = user?.account;
            this.serviceAddress = user?.account?.serviceAddress;
            this.getFullAddress = this.serviceAddress?.getFullAddress();
            this.isOrion = !!this.cimaUserAccountGuid && this.account?.tenantId === 'ORION';

            this.linkedAccountWithActive = this.getLinkedAccount();
            this.activeLinkedAccount = this.getActiveLinkedAccount();
            this.isActiveAccountLinked = !!this.activeLinkedAccount;
        }));
    }

    public closeMobileMenu(): void {
        this.showChildItems = false;
    }

    public onMenuSelect(accountGuid?: string, account?: string, isOrion: boolean = false): void {
        this.showChildItems = false;
        const selectedAccount: string = this.isOrion ? account : accountGuid;
        const activeAccount: string = this.isOrion ? this.activeLinkedAccount?.account : this.activeLinkedAccount?.accountGuid;

        if (!selectedAccount || selectedAccount === activeAccount) {
            return;
        }

        if (isOrion) {
            this.sessionStorage.set(StorageToken.X_TENANT_ID, 'ORION');
            const locationParams: { os?: string; acc?: string } = {
                ...({ os: account }),
                ...(this.state.params.acc && { acc: accountGuid })
            };
            window.location.href = this.state.href('account.activity.landing', locationParams);
        } else {
            this.sessionStorage.set(StorageToken.X_TENANT_ID, '');
            const locationParams: { os?: string; acc?: string } = {
                ...(this.state.params.os && { os: undefined }),
                ...({ acc: accountGuid })
            };
            window.location.href = this.state.href('account.activity.landing', locationParams);
        }
    }

    public expandChildItems(): void {
        this.showChildItems = !this.showChildItems;
        this.showAll = false;
    }

    public onSelectViewAll(): void {
        this.showChildItems = false;
        this.toggleMobileNav.emit();
        const params: StateParams = this.state.params;
        const os: string = params.os;
        const acc: string = params.acc;
        this.state.go('account.activity.switch-locations', {
            ...(os && { os }),
            ...(acc && { acc })
        });
    }

    public mouseHoverEvent(event: Event): void {
        this.showChildItems = event.type === MouseEventType.MOUSE_ENTER;
    }

    public get isBusinessUser(): boolean {
        return this.cimaCore.isBusinessUser;
    }

    public getActiveLinkedAccount(): LocationRow {
        if (this.cimaUserAccountGuid && !this.isOrion) {
            return this.linkedAccounts?.find((acct: LocationRow) => acct.accountGuid === this.cimaUserAccountGuid);
        } else if (this.isOrion) {
            return this.linkedAccounts?.find((acct: LocationRow) => acct.account === this.cimaUserAccountGuid);
        } else {
            return this.linkedAccounts?.find((acct: LocationRow) => acct.accountGuid === this.account?.authGuid);
        }
    }

    public showCheckMarkIcon(accountGuid?: string, account?: string, activeAccountGuid?: string): boolean {
        if (this.isOrion) {
            return account === this.cimaUserAccountGuid;
        } else {
            return accountGuid === activeAccountGuid;
        }
    }

    public getLinkedAccount(): LocationRow[] {
        if (!this.linkedAccounts?.length) {
            return [];
        }
        
        const activeAuthGuid: string = this.cimaUserAccountGuid ?? this.account?.authGuid;
        const key: keyof LocationRow = this.isOrion ? 'account' : 'accountGuid';

        return this.sortActiveFirst(this.linkedAccounts, key, activeAuthGuid).slice(0, this.ACCOUNTS_PREVIEW_LIMIT);
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    private sortActiveFirst(accounts: LocationRow[], key: keyof LocationRow, activeValue: string): LocationRow[] {
        return [
            ...accounts.filter((acct: LocationRow) => acct[key] === activeValue),
            ...accounts.filter((acct: LocationRow) => acct[key] !== activeValue)
        ];
    }
}
